import React, { useState } from 'react';
import { 
  Search, Filter, MoreVertical, Eye, Download, RefreshCw,
  DollarSign, CreditCard, AlertTriangle, CheckCircle, Clock,
  TrendingUp, ArrowUpRight, ArrowDownLeft, Calendar
} from 'lucide-react';

const PaymentManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);

  const payments = [
    {
      id: 'PAY-001',
      orderId: 'ORD-001',
      amount: 2374.25,
      currency: 'USD',
      type: 'payment',
      method: 'Credit Card',
      status: 'completed',
      customer: 'John Smith',
      supplier: 'Bengal Textiles Ltd.',
      date: '2024-02-28',
      processingFee: 71.23,
      netAmount: 2303.02,
      transactionId: 'TXN123456789',
      gateway: 'Stripe'
    },
    {
      id: 'PAY-002',
      orderId: 'ORD-002',
      amount: 1599.00,
      currency: 'USD',
      type: 'payment',
      method: 'PayPal',
      status: 'completed',
      customer: 'Sarah Johnson',
      supplier: 'Eco Jute Co.',
      date: '2024-02-25',
      processingFee: 47.97,
      netAmount: 1551.03,
      transactionId: 'TXN987654321',
      gateway: 'PayPal'
    },
    {
      id: 'PAY-003',
      orderId: 'ORD-003',
      amount: 899.90,
      currency: 'USD',
      type: 'payment',
      method: 'Bank Transfer',
      status: 'pending',
      customer: 'Michael Chen',
      supplier: 'Heritage Weavers',
      date: '2024-02-28',
      processingFee: 26.99,
      netAmount: 872.91,
      transactionId: 'TXN456789123',
      gateway: 'Bank'
    },
    {
      id: 'REF-001',
      orderId: 'ORD-005',
      amount: 1299.50,
      currency: 'USD',
      type: 'refund',
      method: 'Credit Card',
      status: 'completed',
      customer: 'David Brown',
      supplier: 'Bengal Fisheries',
      date: '2024-02-26',
      processingFee: -38.99,
      netAmount: -1260.51,
      transactionId: 'REF789123456',
      gateway: 'Stripe'
    },
    {
      id: 'PAY-004',
      orderId: 'ORD-004',
      amount: 1369.75,
      currency: 'USD',
      type: 'payment',
      method: 'Credit Card',
      status: 'failed',
      customer: 'Emma Wilson',
      supplier: 'Dhaka Leather Works',
      date: '2024-02-27',
      processingFee: 0,
      netAmount: 0,
      transactionId: 'TXN321654987',
      gateway: 'Stripe'
    }
  ];

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.orderId.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         payment.supplier.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
    const matchesType = filterType === 'all' || payment.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleSelectPayment = (paymentId: string) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId) 
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPayments(
      selectedPayments.length === filteredPayments.length 
        ? [] 
        : filteredPayments.map(payment => payment.id)
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="h-3 w-3 mr-1" />
          Pending
        </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Failed
        </span>;
      case 'processing':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <RefreshCw className="h-3 w-3 mr-1" />
          Processing
        </span>;
      default:
        return null;
    }
  };

  const getTypeIcon = (type: string) => {
    return type === 'refund' ? (
      <ArrowDownLeft className="h-4 w-4 text-red-500" />
    ) : (
      <ArrowUpRight className="h-4 w-4 text-green-500" />
    );
  };

  const totalRevenue = payments
    .filter(p => p.status === 'completed' && p.type === 'payment')
    .reduce((sum, p) => sum + p.amount, 0);

  const totalRefunds = payments
    .filter(p => p.status === 'completed' && p.type === 'refund')
    .reduce((sum, p) => sum + Math.abs(p.amount), 0);

  const pendingPayments = payments.filter(p => p.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
              <p className="text-gray-600">Track payments, refunds, and financial transactions</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <Download className="h-4 w-4" />
                <span>Export Report</span>
              </button>
              <button className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex items-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Sync Payments</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by payment ID, order ID, customer, or supplier..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="processing">Processing</option>
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Types</option>
                <option value="payment">Payments</option>
                <option value="refund">Refunds</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ArrowDownLeft className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Refunds</p>
                <p className="text-2xl font-bold text-gray-900">${totalRefunds.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                <p className="text-2xl font-bold text-gray-900">{pendingPayments}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900">94.2%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Transactions ({filteredPayments.length})</h2>
              {selectedPayments.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedPayments.length} selected</span>
                  <button className="text-sm text-orange-600 hover:text-orange-700 font-medium">Export Selected</button>
                </div>
              )}
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPayments.includes(payment.id)}
                        onChange={() => handleSelectPayment(payment.id)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getTypeIcon(payment.type)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{payment.id}</div>
                          <div className="text-sm text-gray-500">Order: {payment.orderId}</div>
                          <div className="text-xs text-gray-400">{payment.date}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{payment.customer}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.supplier}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium ${payment.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                        {payment.type === 'refund' ? '-' : ''}${payment.amount.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Fee: ${Math.abs(payment.processingFee).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Net: ${Math.abs(payment.netAmount).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.method}</div>
                      <div className="text-xs text-gray-500">{payment.gateway}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400 hover:text-gray-600">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600">
                          <Download className="h-4 w-4" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentManagement;